import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PLATFORM_BILLING_MODELS,
  PLATFORM_LICENSING,
  PLATFORM_STATUS_FILTERS,
  activePlatformFilterCount,
  defaultPlatformFilters,
  type PlatformFilters,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { categoryLabel } from "../lib/format";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";
import { Field } from "./ui";

// Filtros de Pago de plataformas: rango de meses, cliente, tipo de plataforma,
// tipo de suscripción (modelo de cobro), licenciamiento y estado.

export default function PlatformFiltersBar({
  value,
  onChange,
  clients,
  categories,
  currentMonth,
  shown,
  total,
}: {
  value: PlatformFilters;
  onChange: (next: PlatformFilters) => void;
  clients: string[];
  categories: string[];
  currentMonth: string;
  shown: number;
  total: number;
}) {
  const { i18n } = useTranslation("common");
  const { t: tp } = useTranslation(PLATFORMS_NS);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const set = (k: keyof PlatformFilters) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  // Donde <input type="month"> se ve como texto (Firefox/Safari de escritorio), lo tecleado a medias
  // se guarda aquí y solo se aplica cuando ya es un mes válido (o se borra).
  const [draft, setDraft] = useState({ from: value.from, to: value.to });
  useEffect(() => setDraft({ from: value.from, to: value.to }), [value.from, value.to]);
  const setMonth = (k: "from" | "to") => (e: { target: { value: string } }) => {
    const v = e.target.value;
    setDraft((d) => ({ ...d, [k]: v }));
    if (v === "" || /^\d{4}-(0[1-9]|1[0-2])$/.test(v)) onChange({ ...value, [k]: v });
  };
  const active = activePlatformFilterCount(value);
  const def = defaultPlatformFilters(currentMonth);
  const isDefault = active === 0 && value.from === def.from && value.to === def.to;

  return (
    <div className={c.card} role="search" aria-label={tp("filters.title")} data-testid="platform-filters">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Field label={tp("filters.from")}>
          <input type="month" className={c.input} value={draft.from} max={value.to} placeholder="AAAA-MM" onChange={setMonth("from")} />
        </Field>
        <Field label={tp("filters.to")}>
          <input type="month" className={c.input} value={draft.to} min={value.from} max={currentMonth} placeholder="AAAA-MM" onChange={setMonth("to")} />
        </Field>
        <Field label={tp("filters.client")}>
          <select className={c.input} value={value.client} onChange={set("client")}>
            <option value="">{tp("filters.all")}</option>
            {clients.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tp("filters.category")}>
          <select className={c.input} value={value.category} onChange={set("category")}>
            <option value="">{tp("filters.all")}</option>
            {categories.map((x) => (
              <option key={x} value={x}>
                {categoryLabel(x, i18n.language)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tp("filters.model")}>
          <select className={c.input} value={value.model} onChange={set("model")}>
            <option value="">{tp("filters.all")}</option>
            {PLATFORM_BILLING_MODELS.map((m) => (
              <option key={m} value={m}>
                {tp(`model.${m}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tp("filters.licensing")}>
          <select className={c.input} value={value.licensing} onChange={set("licensing")}>
            <option value="">{tp("filters.all")}</option>
            {PLATFORM_LICENSING.map((l) => (
              <option key={l} value={l}>
                {tp(`licensing.${l}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tp("filters.status")}>
          <select className={c.input} value={value.status} onChange={set("status")}>
            {PLATFORM_STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {tp(`filters.status_${s}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 text-xs ${c.muted}`}>
        <span data-testid="platform-filters-count">{tp("filters.showing", { shown, total })}</span>
        {!isDefault && (
          <button type="button" className="text-teal-600 hover:underline" onClick={() => onChange(def)}>
            <i className="ri-filter-off-line mr-1" />
            {tp("filters.clear")}
          </button>
        )}
      </div>
    </div>
  );
}
